import * as React from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import filtersMap from '~/settings/filtersMap';
import { flat, isMobileOrTabletWindow } from '~/utils/utils';
import { scrollTop } from '~/utils/scrollTop';
import dwhExport from '~/api/dwhExport';
import { DWH_EVENTS } from '~/const';
import styles from '~/components/CategoryFilter/CategoryFilter.scss';
import FiltersContent from '~/components/CategoryFilter/FiltersContent';
import { t } from '~/utils/localization';
import { IFilter } from '~/components/CategoryFilter/CategoryFilter';
import { DivTooltip } from '@wg/wows-react-uikit';
import DefaultTooltip from '~/components/Tooltip/DefaultTooltip';
import Tag from '~/components/Tag/Tag';
import classNames from 'classnames';
import { FilterPosition } from '~/types/category';
import { playDropdownClickSound } from '~/api/WoWsClient';
import { RootState, useAppSelector } from '~/Store';
import { appActions, updateCategoryByFilters } from '~/Store/appSlice';

interface IFilterPopup extends IFilter {
    onChangeVisible: () => void;
    category: ICategoryList;
    isDisplayDefault?: boolean;
    changeFilterPosition?: (position: FilterPosition) => void;
}

const stateSelectorFiltersPopup = (state: RootState) => {
    return {
        filters: state.app.filters,
    };
};

const FiltersPopup = React.forwardRef((props: IFilterPopup, ref: React.Ref<HTMLDivElement>) => {
    const dispatch = useDispatch();
    const filterNames = filtersMap[props.category];

    const onTouchEnd = (event: React.TouchEvent) => {
        event.preventDefault();
        props.onChangeVisible();
    };

    const app = useAppSelector(stateSelectorFiltersPopup, shallowEqual);
    const filters: Record<string, string[]> = app.filters?.[props.category] || {};
    const [activeFiltersMap, setFilter] = React.useState<Record<string, string[]>>({});
    const hasActiveFiltersValues = !!flat(Object.values(filters || {}))?.length;

    React.useEffect(() => {
        setFilter(filters);
    }, []);

    const onChangeFilter = (name: FILTER_INFO_NAME, value: string) => {
        if (!isMobileOrTabletWindow) {
            scrollTop(0, true);
        }

        dispatch(appActions.changeFilter({ category: props.category, name, value }));
        dispatch(updateCategoryByFilters(props.category, name, value));

        dwhExport.send(DWH_EVENTS.FILTERS_APPLY, {
            category: props.category,
            name: name,
            value: value,
        });
    };

    const Buttons = (
        <React.Fragment>
            {hasActiveFiltersValues && (
                <Tag
                    label={t('Сбросить все')}
                    showCloseIcon={true}
                    tooltipStyles={!hasActiveFiltersValues && { cursor: 'initial' }}
                    onClick={() => {
                        scrollTop(0, true);
                        dispatch(appActions.resetCategoryFilter({ category: props.category }));
                    }}
                    classNames={{
                        tag: classNames(styles.buttonReset, {
                            [styles.buttonDisabled]: !hasActiveFiltersValues,
                        }),
                    }}
                />
            )}
            <Tag
                label={t('Применить')}
                onClick={props.onChangeVisible}
                classNames={{
                    tag: styles.buttonApply,
                }}
            />
        </React.Fragment>
    );

    return (
        <div className={styles.filterContainer} ref={ref}>
            {props.isDisplayDefault && (
                <DivTooltip className={styles.buttonPosition} tooltipBody={<DefaultTooltip text={t('Отображать фильтры сбоку')} />}>
                    <div
                        className={styles.filterColumnRight}
                        onClick={(event) => {
                            event.stopPropagation();
                            playDropdownClickSound();
                            props.changeFilterPosition?.(FilterPosition.RIGHT);
                        }}
                    />
                </DivTooltip>
            )}
            <div className={styles.filterContainerWrapper}>
                <div className={styles.popupClose} onClick={props.onChangeVisible} onTouchEnd={onTouchEnd} />
                {!!filterNames && <FiltersContent category={props.category} onChangeFilter={onChangeFilter} state={activeFiltersMap} />}
                {isMobileOrTabletWindow && <div className={styles.mobileButtonSaveFilters}>{Buttons}</div>}
            </div>
            <div className={styles.buttonApplyWrapper}>{Buttons}</div>
        </div>
    );
});

export default FiltersPopup;
